import type { ComponentType } from "react";
import type { PermissionCode } from "@koclukpanel/authz";
import { Denemeler } from "../Denemeler";
import { HaftalikAnaliz } from "../HaftalikAnaliz";
import { HataDefteri } from "../HataDefteri";
import { KoclukGorusmeleri } from "../KoclukGorusmeleri";
import { KonuIlerlemesi } from "../KonuIlerlemesi";
import { KonuPlani } from "../KonuPlani";
import { OgrenciDosyasi } from "../OgrenciDosyasi";
import { OgrenciKaynaklari } from "../OgrenciKaynaklari";
import { OgrenciPlanlari } from "../OgrenciPlanlari";
import { Program } from "../Program";
import { Raporlar } from "../Raporlar";
import { GenelBakis } from "./GenelBakis";
import { IslemGecmisi } from "./IslemGecmisi";
import { Notlar } from "./Notlar";
import { PaketBolumu } from "./PaketHaklari";
import { TaleplerSekmesi } from "./TaleplerSekmesi";

/**
 * ÖĞRENCİ 360° — SEKME KAYIT DEFTERİ (F-PPL-04)
 *
 * ================================================================
 * NEDEN TEK DOSYA VE NEDEN İZİN KODU TİPTE ZORUNLU
 * ================================================================
 * On dört sekmenin her biri ayrı bir yetki sorusu. Bu soruyu her
 * sekmenin KENDİ içinde sormak, on dört kez hatırlanması gereken bir
 * kural demekti — bu depoda defalarca görülen sınıf: kuralı bilmemek
 * değil, kuralı HATIRLAMAYA bırakmak.
 *
 * Burada hatırlanacak bir şey yok: `izin` alanı `OgrenciSekmesi`
 * tipinde ZORUNLU ve `PermissionCode` tipinde. Yani
 *
 *   · izin kodu yazılmamış bir sekme DERLENMEZ,
 *   · boş dizi (`izin: []`) DERLENMEZ — tip en az bir eleman istiyor,
 *   · yazım hatası (`kisi.ogrenci.goruntul`) DERLENMEZ.
 *
 * Kabuk (`Ogrenci360.tsx`) bu listeyi süzüyor: izni olmayan sekme
 * ÇİZİLMİYOR — devre dışı düğme de yok, "yetkiniz yok" kutusu da yok.
 * Boş bir kutu, o verinin VAR OLDUĞUNU söyler; asıl saklanması gereken
 * çoğu zaman budur.
 *
 * ================================================================
 * BU SÜZGEÇ BİR KORUMA DEĞİL, BİR NEZAKET
 * ================================================================
 * Verinin korunması SUNUCUDA: her sekmenin çağırdığı uç kendi
 * `@RequirePermission` dekoratörünü ve kendi kayıt bazlı kapsam
 * kontrolünü taşıyor. Buradaki süzgeç yalnızca kullanıcıyı, açtığında
 * 403 alacağı bir sekmeyle karşılaştırmamak için var.
 *
 * Ayrım önemli: bu dosyadaki bir hata ekranı YANLIŞ gösterir, veriyi
 * SIZDIRMAZ. Sızdırabilseydi koruma yanlış yerde olurdu.
 *
 * ================================================================
 * "NOTLAR" EN SONA KALDI — VE SEBEBİ ÖĞRETİCİ
 * ================================================================
 * F-PPL-04'ün on dört sekmesi tamam. Notlar en sona kaldı çünkü
 * `StudentNote` F-SES-04 görünürlük seviyesi taşıyor ve seviyelerden
 * biri `PROTECTED` — "sadece yazan + gerekçeli yönetici erişimi".
 *
 * Sekmeyi "izne göre süzülen bir liste" olarak yazmak, korumalı notun
 * kendi kapısının (şifre + gerekçe + `ProtectedAccessLog`) ETRAFINDAN
 * dolaşan ikinci bir kapı açardı. Önce kapı tasarlandı, sonra sekme:
 * liste ucu korumalı gövdeyi HİÇ OKUMUYOR (`not.service.ts`).
 */

export interface SekmeOzellikleri {
  ogrenciId: string;
  ogrenciAdi: string;
  /**
   * Sekmenin İÇİNDEKİ alt yetkiler için.
   *
   * Ham `Record<string, string>` yerine fonksiyon: katalogda olmayan
   * bir dizgiyi sormak DERLENMİYOR.
   *
   * Bu desen 05.09.2026'da panelin TAMAMINA yayıldı (`izin.ts`): o gün
   * yirmi üç yerde elle yazılmış izin dizgisi vardı ve hiçbiri
   * korunmuyordu. Tarama bir gerçek hata buldu —
   * `Uygulama.tsx` -> `program.hafta.duzenle`, katalogda böyle bir izin
   * yok, satır yazıldığından beri hep `false` dönüyordu.
   */
  izinVar: (kod: PermissionCode) => boolean;
  /**
   * Öğrenci seçimini bırakıp listeye dönmek.
   *
   * Mevcut ekranların çoğu `onGeri` bekliyor; 360° kabuğunda "geri"
   * ancak "başka öğrenci" anlamına gelebilir — sekmeler arası gezinme
   * zaten şeritte.
   */
  onOgrenciDegistir: () => void;
}

export interface OgrenciSekmesi {
  /** Adres çubuğuna ve `id` üretimine giren kararlı anahtar */
  anahtar: string;
  etiket: string;
  /**
   * Sekmeyi görmek için gereken izinlerden EN AZ BİRİ.
   *
   * Dizi, çünkü bazı uçlar zaten çoklu izin istiyor — abonelik ucu
   * `@RequirePermission(["finans.abonelik.ozet",
   * "finans.tahsilat.goruntule"])`. Tek koda indirgemek, o uçta yetkisi
   * olan bir kullanıcıya sekmeyi göstermezdi.
   *
   * Tip "en az bir eleman" diyor: boş dizi derlenmiyor, yani izinsiz
   * sekme YAZILAMIYOR.
   */
  izin: readonly [PermissionCode, ...PermissionCode[]];
  Icerik: ComponentType<SekmeOzellikleri>;
}

export const OGRENCI_SEKMELERI: readonly OgrenciSekmesi[] = [
  {
    anahtar: "genel-bakis",
    etiket: "Genel Bakış",
    // Ekranin acilma izniyle AYNI: bu sekmeyi goren herkes zaten
    // ogrenciyi gorebiliyor demektir.
    izin: ["kisi.ogrenci.goruntule"],
    Icerik: GenelBakis,
  },
  {
    anahtar: "program",
    etiket: "Program",
    izin: ["program.hafta.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, onOgrenciDegistir }) => (
      <Program ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} onGeri={onOgrenciDegistir} />
    ),
  },
  {
    anahtar: "konular",
    etiket: "Konu ve Müfredat",
    /*
      IKI IZIN, IKI ALT BOLUM: konu ilerlemesi ucu
      `kisi.ogrenci.goruntule`, konu plani ucu `akademik.plan.goruntule`
      istiyor. Sekme ikisinden BIRI varsa aciliyor; icerideki bolumler
      kendi izinlerine AYRI AYRI bakiyor — yani sekmeyi acabilmek, iki
      bolumu de gorebilmek anlamina gelmiyor.
    */
    izin: ["kisi.ogrenci.goruntule", "akademik.plan.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, izinVar }) => (
      <div className="flex flex-col gap-6">
        {izinVar("kisi.ogrenci.goruntule") && (
          <KonuIlerlemesi ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} />
        )}
        {izinVar("akademik.plan.goruntule") && (
          <KonuPlani ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} />
        )}
      </div>
    ),
  },
  {
    anahtar: "kaynaklar",
    etiket: "Kaynaklar",
    izin: ["akademik.kaynak.ogrenci_listesi"],
    Icerik: ({ ogrenciId, ogrenciAdi, izinVar }) => (
      <OgrenciKaynaklari
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        duzenleyebilir={izinVar("akademik.kaynak.ogrenci_duzenle")}
        kaldirabilir={izinVar("akademik.kaynak.ogrenci_kaldir")}
      />
    ),
  },
  {
    anahtar: "denemeler",
    etiket: "Denemeler",
    izin: ["deneme.sonuc.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, onOgrenciDegistir }) => (
      <Denemeler ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} onGeri={onOgrenciDegistir} />
    ),
  },
  {
    anahtar: "gorusmeler",
    etiket: "Görüşmeler",
    izin: ["gorusme.kayit.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, izinVar }) => (
      <KoclukGorusmeleri
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        notYazabilir={izinVar("gorusme.not_korumali.yaz")}
        notOkuyabilir={izinVar("gorusme.not_korumali.oku")}
      />
    ),
  },
  {
    anahtar: "istatistik",
    etiket: "Çalışma İstatistikleri",
    izin: ["analiz.ogrenci.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi }) => (
      <HaftalikAnaliz ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} />
    ),
  },
  {
    anahtar: "veli-raporlari",
    etiket: "Veli Raporları",
    // Uc ikisini de kabul ediyor (`rapor.controller.ts`): raporu
    // olusturan uzman ve onaylayan koordinator AYRI izinler tasiyor.
    izin: ["rapor.veli.olustur", "rapor.veli.onayla"],
    Icerik: ({ ogrenciId, ogrenciAdi, onOgrenciDegistir }) => (
      <Raporlar ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} onGeri={onOgrenciDegistir} />
    ),
  },
  {
    anahtar: "hata-defteri",
    etiket: "Hatalı Soru Defteri",
    izin: ["calisma.hatadefteri.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi }) => (
      <HataDefteri ogrenciId={ogrenciId} ogrenciAdi={ogrenciAdi} />
    ),
  },
  {
    anahtar: "talepler",
    etiket: "Talepler",
    /*
      UC AYRI IZIN, IKI AYRI TABLO. Genel talepler (`StudentRequest`)
      ucu uc izinden birini kabul ediyor — talebi ACAN da, karara
      BAGLAYAN da, finans tarafi da ayri izin tasiyor
      (`operasyon.controller.ts`). Program degisiklikleri
      (`ScheduleChangeRequest`) ayri bir tablo ve ayri bir izin.
      Sekme dordunden biri varsa aciliyor; icerideki iki bolum kendi
      iznine AYRI AYRI bakiyor.
    */
    izin: [
      "operasyon.talep.olustur",
      "operasyon.talep.karara_bagla",
      "finans.talep.karara_bagla",
      "program.degisiklik.goruntule",
    ],
    Icerik: TaleplerSekmesi,
  },
  {
    anahtar: "notlar",
    etiket: "Notlar",
    /*
      ================================================================
      SEKME `kisi.ogrenci.goruntule` ILE ACILIYOR — VE BU YETERLI
      ================================================================
      Notun GORUNURLUGU kayit bazinda (F-SES-04): ogrenci `STUDENT`
      seviyesindekini gorur, veli `PARENT`i, personel `INTERNAL`i.
      Sekmeyi daha dar bir izne baglasaydik, kendisine yazilmis bir notu
      okuyabilecek ogrenci sekmeyi HIC goremezdi.

      Korumali notun govdesi bu sekmeden CIKMIYOR: ayri uc, ayri izin
      (`gorusme.not_korumali.oku`) ve o izin sudo kapisini aciyor.
    */
    izin: ["kisi.ogrenci.goruntule"],
    Icerik: Notlar,
  },
  {
    anahtar: "dosyalar",
    etiket: "Dosyalar ve Veliler",
    izin: ["kisi.ogrenci.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, izinVar }) => (
      <OgrenciDosyasi
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        veliBaglayabilir={izinVar("kisi.veli.bagla")}
        veliKaldirabilir={izinVar("kisi.veli.kaldir")}
        duzenleyebilir={izinVar("kisi.ogrenci.duzenle")}
      />
    ),
  },
  {
    anahtar: "paket",
    etiket: "Paket ve Ödeme",
    izin: ["finans.abonelik.ozet", "finans.tahsilat.goruntule"],
    Icerik: ({ ogrenciId, izinVar }) => (
      <PaketBolumu ogrenciId={ogrenciId} acabilir={izinVar("finans.tahsilat.isle")} />
    ),
  },
  {
    anahtar: "islem-gecmisi",
    etiket: "İşlem Geçmişi",
    /*
      ================================================================
      BU SEKME İZİN VARLIĞIYLA YETİNMİYOR — KAPSAM DA GEREKİYOR
      ================================================================
      Uç ölçüldü (`etkinlik.service.ts` -> `kisi`): BAŞKASININ dökümü
      için izin `ALL` kapsamıyla taşınmalı, üstüne yazılı gerekçe
      gerekiyor ve açılış SENKRON bir `SECURITY` olayı olarak
      kaydediliyor.

      Kayıt defteri yalnızca izin VARLIĞINA bakıyor; kapsamı burada
      soramıyor. Dar kapsamlı biri sekmeyi görürse ne olur? Sekmenin
      kendisi künyeden kimlik gelmediğini görüp sebebi yazıyor — boş
      bir panel bırakmıyor. Kapsam kontrolü künyenin alanı döndürüp
      döndürmemesinde, yani SUNUCUDA.
    */
    izin: ["denetim.etkinlik.kisi"],
    Icerik: IslemGecmisi,
  },
  {
    anahtar: "okul-ders-saatleri",
    etiket: "Okul ve ders saatleri",
    /*
      UI/UX Dalga 2 (M2): "Ogrenci planlari" menuden kalkti (ogrenci baglamli
      ekranlar yalnizca 360° sekmesi); bu sekme olmasaydi okul/dershane/ozel ders
      saatlerine ulasmanin yolu KAYBOLURDU. Duzenleme izni sekmenin icinde ayrica
      soruluyor (`takvim.mesguliyet.yonet`); sunucu her yazmada yine kontrol ediyor.
    */
    izin: ["takvim.mesguliyet.goruntule"],
    Icerik: ({ ogrenciId, ogrenciAdi, izinVar }) => (
      <OgrenciPlanlari
        ogrenciId={ogrenciId}
        ogrenciAdi={ogrenciAdi}
        duzenleyebilir={izinVar("takvim.mesguliyet.yonet")}
      />
    ),
  },
];

/**
 * GÖRÜNÜR SIRA — UI/UX Dalga 2 (M2, Emre kararı D4, 06.10.2026)
 *
 * Kayıt defterinin sırası ekleme sırası; EKRANDA görünen sıra burası. İlk beşi
 * şeritte, kalanı "Diğer" menüsünde: günlük işin dönüp durduğu beş bölüm
 * (Genel Bakış, Program, Görüşmeler, Denemeler, Konu ve Müfredat) öne alındı,
 * yılda birkaç kez açılanlar (paket, işlem geçmişi…) menüye. Kullanım sıklığı
 * VERİSİ yok (mutabakat §6): beşli bir öneridir, ölçüm gelince değişir.
 *
 * "İlk beş" izne göre süzülmüş listenin ilk beşi: görüşme izni olmayan kişide
 * Görüşmeler yerine sıradaki izinli bölüm öne çıkıyor.
 */
export const SEKME_SIRASI: readonly string[] = [
  "genel-bakis",
  "program",
  "gorusmeler",
  "denemeler",
  "konular",
  "kaynaklar",
  "istatistik",
  "veli-raporlari",
  "hata-defteri",
  "talepler",
  "notlar",
  "dosyalar",
  "paket",
  "islem-gecmisi",
  "okul-ders-saatleri",
];

/** Şeritte doğrudan görünen sekme sayısı; fazlası "Diğer" menüsüne. */
export const GORUNEN_SEKME_ADEDI = 5;
